import { translateI18n } from '~/common/utils/i18n-helper';
/* 订单域通用异步方法
 ** 使用说明：部分方法需要在订单域通用，但又不在整个web项目通用，可以在此定义。已在common/inject.js中挂载进全局
 ** 调用方法：如 this.$orderActions.axiosPost(传参)
 ** 特殊说明：异步调用或者需要挂载this上才在这里写入，同步方法在隔壁utils.js中声明
 **/

import Vue from 'vue';
import { checkAuth } from './utils.js';
import { apiGetShopMemberList, apiCart, apiGetReturnReasonList, apiGetWarehouseList, apiGetAccountBalances, apiGetQcRuleList } from './api.js';
import { orderModuleConfig, defaultShowDetailInfo } from './const.js';
import DialogApproveReason from '../components/dialog/DialogApproveReason.vue';
import i18n from '~/common/plugins/i18n.js';
class OrderActions {
  // 把通用对象挂进class对象的this上
  constructor({
    vueProto,
    vueStore
  }) {
    this.vueProto = vueProto;
    this.vueStore = vueStore;
    // 创建一个审批弹窗dom，给下面的approvalReason方法调用，以防止重复生成dom
    this.approvalReasonDom = null;
  }

  // 去除请求数据中的code、msg，直接拿到data
  async removeCode(axiosPromise) {
    const resData = await axiosPromise;
    if (resData?.code === 200) {
      return Promise.resolve(resData.data);
    }
    return Promise.reject(resData);
  }

  // axios的post方法
  axiosPost(url, params, headers) {
    return this.removeCode(this.vueProto.$axios.$post(url, params, headers));
  }

  // axios的get方法
  axiosGet(url, params, headers) {
    return this.removeCode(this.vueProto.$axios.$get(url, params, headers));
  }

  // axios的put方法
  axiosPut(url, params, headers) {
    return this.removeCode(this.vueProto.$axios.$put(url, params, headers));
  }

  // 查询区域
  async getRegions() {
    const resData = await this.axiosPost(apiCart.regionsSearch).catch(err => 'error');
    if (resData === 'error') return [];
    return resData?.list || [];
  }

  // 查询买卖车信息
  async getCartInfo({
    match_type = 'P',
    region_id = ''
  }) {
    const resData = await this.axiosGet(apiCart.carts, {
      match_type,
      region_id
    }).catch(err => 'error');
    if (resData === 'error') return [];
    return resData || [];
  }

  // 查询交易员列表
  async getShopMemberList({
    name = '',
    current = 1,
    page_size = 50
  }) {
    const resData = await this.axiosPost(apiGetShopMemberList, {
      name,
      current,
      page_size
    }).catch(() => 'error');
    if (resData === 'error') return [];
    return resData?.list || [];
  }

  // 获取交易员列表并存进页面中的shopMemberList，该方法给筛选条件用
  async filterShopMemberList(name, vue) {
    const newList = await this.getShopMemberList({
      name,
      current: 1,
      page_size: 50
    });
    vue.shopMemberList = newList.map(item => ({
      id: item.id,
      name: item.short_name
    }));
  }

  // 查询品控列表
  async getQcRuleList({
    current = 1,
    page_size = 100
  } = {}) {
    const resData = await this.axiosPost(apiGetQcRuleList, {
      current,
      page_size
    }).catch(() => 'error');
    if (resData === 'error') return [];
    return resData?.list || [];
  }

  // 查询退货原因列表
  async getReturnReasonList() {
    const resData = await this.axiosPost(apiGetReturnReasonList).catch(() => 'error');
    if (resData === 'error') return [];
    const arrData = resData?.list || [];
    return arrData.map(item => ({
      ...item,
      name: item.label
    }));
  }

  // 查询仓库列表
  async getWarehouseList(params = {
    current: 1,
    page_size: 100,
    active: true
  }) {
    const resData = await this.axiosPost(apiGetWarehouseList, params).catch(() => 'error');
    if (resData === 'error') return [];
    return resData?.list || [];
  }

  // 查询订单账户的卡余额
  async getAccountBalance(params) {
    const resData = await this.axiosPost(apiGetAccountBalances, params).catch(() => 'error');
    if (resData === 'error') return 0;
    return resData?.balances || 0;
  }

  /* 【打开详情页通用方法】
   ** 参数说明：
   ** {
   **   openType: String, // 通过什么方式打开[非必须，不传默认为'click']：'click'代表通过点击表格行打开；'route'代表路由参数带了id直接打开
   **   moduleName: String, // 详情页所属的模块名称，会根据这个查询对应的authRoute进行详情页鉴权[必须]
   **   objectId: String, // 详情页关键id，来自于表格行的主键id或者路由中的参数id[路由打开时非必须，表格行打开时必须]
   **   rowData: Object, // 表格行点击打开时的行数据[路由打开时非必须，表格行打开时必须]
   **   drawerLevel: Number, // 详情页的层级[非必须，不传默认为1]
   ** }
   ** 特殊说明：
   ** 1. 为节省代码，该方法高度封装，需要各参数、对象命名统一，并通过call方法直接传this进来
   */
  async openDetail({
    openType = 'click',
    moduleName = '',
    objectId = '',
    rowData = {},
    drawerLevel = 1
  } = {}) {
    const checkPromise = new Promise((resolve, reject) => {
      // 先获取到是否有权限打开详情，然后再根据打开详情页的类型来做出不同处理
      let resolveData = {};
      const {
        query = {},
        path = ''
      } = this.$route;
      const detailInfo = drawerLevel === 1 ? this.showDetailInfo : this.showSubInfo; //浅拷贝，通过这里，修改了传到详情页中的showDetailInfo
      const detailName = orderModuleConfig[moduleName].detailName;
      const authRoute = orderModuleConfig[moduleName].authRoute;
      const objectType = orderModuleConfig[moduleName].objectType;
      const ifHasAuth = checkAuth({
        authRoute,
        authCode: 'read'
      });
      // 如果是路由传参id打开
      if (openType === 'route') {
        const queryId = query.id || '';
        const queryTab = query.tab || '';
        if (!queryId) {
          return reject('fail');
        }
        if (!ifHasAuth) {
          this.$message.warning({
            message: translateI18n('portal.wu-quan-xian-fang-wen-xiang-qing-ye'),
            duration: 1500
          });
          return reject('fail');
        }
        resolveData = {
          openType,
          moduleName,
          objectId: queryId,
          objectType,
          rowData: {
            id: queryId
          },
          authRoute,
          drawerLevel,
          detailName,
          queryTab
        };
        Object.assign(detailInfo, resolveData);
        return resolve(resolveData);
      }
      // 如果是点击表格行打开
      if (openType === 'click') {
        if (!ifHasAuth) {
          this.$message.warning({
            message: translateI18n('portal.wu-quan-xian-fang-wen-xiang-qing-ye'),
            duration: 1500
          });
          return reject('fail');
        }
        if (drawerLevel === 1) {
          this.$router.push({
            path,
            query: {
              ...query,
              id: objectId
            }
          });
        }
        resolveData = {
          openType,
          moduleName,
          objectId,
          objectType,
          rowData,
          authRoute,
          drawerLevel,
          detailName
        };
        Object.assign(detailInfo, resolveData);
        return resolve(resolveData);
      }
    });
    return checkPromise;
  }

  /* 【关闭详情页后的操作。比如清空配置数据、还原链接地址等】
   ** 参数说明：
   ** drawerLevel: Number // 当前详情页的层级[必须]
   ** 特殊说明：
   ** 1.为节省代码，该方法透传了this进来，需要用call调用并保持showDetailInfo 和 showSubInfo命名统一
   */
  closeDetail({
    drawerLevel = 1
  } = {}) {
    const detailInfo = drawerLevel === 1 ? this.showDetailInfo : this.showSubInfo;
    Object.assign(detailInfo, {
      ...defaultShowDetailInfo
    });
    const {
      query = {},
      path = ''
    } = this.$route;
    if (drawerLevel === 1 && query.id) {
      const cloneQuery = {
        ...query
      };
      delete cloneQuery.id;
      this.$router.push({
        path,
        query: cloneQuery
      });
    }
  }

  /* 【新窗口打开业务域详情页。用于点击“成交数”的交互】
   ** 参数说明：
   ** {
   **   tradeMethod: String, // 交易方式，见./const.js中tradeMethodList[必须]
   **   moduleName: String, // 在哪个模块点击跳转，用于判断跳哪个详情页[必须，关键参数]
   **   saleId: String, // 对应的销售项主键id[必须]
   **   purchaseId: String, // 对应的采购项主键id[必须]
   ** }
   */
  openBuyDetailPage({
    tradeMethod,
    moduleName,
    saleId,
    purchaseId
  } = {}) {
    const platSelfType = ['platform', 'self-operated'];
    const saleDetailPath = `/store/buy/sales_order_line?id=${saleId}&match_id=${saleId}`;
    const purchaseDetailPath = `/store/sell/purchase_order_line?id=${purchaseId}&match_id=${purchaseId}`;
    switch (moduleName) {
      // 在订单域“销售项”列表点击的，“平台”、“自营”类型打开采购项详情，其他打开销售项详情
      case 'saleItem':
        window.open(platSelfType.includes(tradeMethod) ? purchaseDetailPath : saleDetailPath, '_blank');
        break;
      // 在订单域“采购项”列表点击的，“平台”、“自营”类型打开销售项详情，其他打开采购项详情
      case 'purchaseItem':
        window.open(platSelfType.includes(tradeMethod) ? saleDetailPath : purchaseDetailPath, '_blank');
        break;
      default:
        break;
    }
  }

  /* 【表格内的表单校验报错提示，做成动画形式】
   ** 参数说明：
   ** formDom: Dom对象, // 表单dom[必须]
   */
  showFormValidate(formDom) {
    if (!formDom) return;
    const errDoms = formDom.querySelectorAll('.el-form-item__error');
    if (errDoms.length === 0) return;
    const arrDoms = [...errDoms];
    arrDoms.forEach(item => {
      const arrCls = [...item.classList];
      if (arrCls.includes('removeAni')) return;
      item.classList.add('removeAni');
      setTimeout(() => {
        item.classList.remove('removeAni');
      }, 100);
    });
  }

  /*【直接用语法唤起审批理由弹窗】
   ** 使用说明：页面中直接调用this.$orderActions.approvalReason(dialogParam)
   ** dialogParam：同element-ui的dialog的参数，也可以传自定义的参数进去
   */
  approvalReason(dialogParam) {
    // 创建一个虚拟节点，扩展组件
    const confirmConstructor = Vue.extend(DialogApproveReason);
    if (!this.approvalReasonDom) {
      this.approvalReasonDom = new confirmConstructor({
        i18n, // 传递 i18n 实例
        el: document.createElement('div')
      });
    }
    document.body.appendChild(this.approvalReasonDom.$el);
    // 在这里更新自定义参数
    this.approvalReasonDom.updateDialogParam(dialogParam);
  }
}
export default OrderActions;
